/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck - Exempt from typecheck; undergoing changes in a different branch
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { ReactGrid } from '@codametrix/grid';
import {
  CellClickedEvent,
  RowClickedEvent,
  GridOptions,
  RowSelectedEvent,
  SelectionChangedEvent,
  ColDef,
  GridApi,
  GridReadyEvent
} from '@codametrix/grid';
import { Box } from '@chakra-ui/react';
import { EndpointData, getDataSource } from './data-grid-utils';
import { useDispatch } from 'react-redux';
import { CustomLoadingCellRenderer } from './custom-loading-cell-renderer';

export type DataGridProps<TData = any> = {
  rowData?: TData[];
  columnDefs: ColDef[];
  onRowClicked?: (e: RowClickedEvent) => void;
  onCellClicked?: (e: CellClickedEvent<TData, TData>) => void;
  onRowSelected?: (e: RowSelectedEvent) => void;
  onSelectionChanged?: (e: SelectionChangedEvent) => void;
  autoSizeColumns?: boolean;
  gridOptions?: GridOptions;
  serverSideInfiniteScroll?: boolean;
  paginationPageSize?: number;
  cacheBlockSize?: number;
  filterableOptions?: CMxCommonApp.FilterableOption;
  contentFilter?: Function;
  endpointData?: EndpointData;
  loaderInfo?: string;
  bodyObject?: object;
  customHeader?: any;
  height?: string | number;
  showLoadingOverlay?: boolean;
};

const DataGrid = ({
  rowData,
  columnDefs,
  onRowClicked,
  onSelectionChanged,
  onCellClicked,
  onRowSelected,
  gridOptions,
  serverSideInfiniteScroll,
  paginationPageSize,
  cacheBlockSize,
  filterableOptions,
  contentFilter,
  endpointData,
  loaderInfo,
  bodyObject,
  customHeader,
  height,
  showLoadingOverlay = true
}: DataGridProps) => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      (filterableOptions || bodyObject) &&
      endpointData?.endPoint &&
      serverSideInfiniteScroll
    ) {
       gridApi?.setGridOption('cacheBlockSize', paginationPageSize ?? 25);
       gridApi?.setGridOption('paginationPageSize', paginationPageSize ?? 25);
      const datasource = getDataSource(
        endpointData,
        filterableOptions,
        paginationPageSize ?? 25,
        dispatch,
        contentFilter,
        loaderInfo,
        bodyObject,
        showLoadingOverlay
      );
      gridApi?.setGridOption('serverSideDatasource', datasource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterableOptions,
    gridApi,
    paginationPageSize,
    serverSideInfiniteScroll,
    contentFilter,
    bodyObject,
    showLoadingOverlay
  ]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  useEffect(() => {
    if (gridApi && rowData) {
      gridApi.setGridOption('rowData', rowData);
    }
  }, [gridApi, rowData]);

  const { defaultColDef: gridOptionsDefaultColDef, sortingOrder, ...restGridOptions } = gridOptions ?? {};
  const pageSize = paginationPageSize ?? 25;
  const agGridOptions: GridOptions = {
    columnDefs: columnDefs,
    domLayout:'normal',
    defaultColDef: {
      flex: 1,
      lockPinned: true,
      sortable: true,
      ...(sortingOrder && { sortingOrder }),
      ...gridOptionsDefaultColDef
    },
    components: {
      agColumnHeader: customHeader
    },
    suppressMovableColumns: true,
    suppressCellFocus: true,
    suppressHorizontalScroll: true,
    pagination: true,
    paginationPageSize: pageSize,
    paginationPageSizeSelector: [25, 50, 100],
    rowModelType: serverSideInfiniteScroll ? 'serverSide' : 'clientSide',
    ...(serverSideInfiniteScroll && { cacheBlockSize: cacheBlockSize ?? 25 }),
    multiSortKey: 'ctrl',
    ...restGridOptions
  };

  const gridRef = useRef<ReactGrid>(null);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption('columnDefs', columnDefs);
    }
  }, [columnDefs]);

  const loadingCellRenderer = useMemo(() => {
    return CustomLoadingCellRenderer;
  }, []);

  return (
    // This container wraps the react grid because a height is required on the surrounding container for the 
    // ag grid stuff to calculate properly for the rows. There is an override prop for the component in case
    // someone needs to change height where it's used.
    // It is not included in the grid package itself because dashboard and service desk use different layout frameworks.
    <Box 
      className="dashboard-data-grid" 
      height={height ?? '100%'}
      width="100%"
      display="flex"
      flexDirection="column"
      flex= "1 1 0"
      minHeight={height ? 0 : "200px"}
    >
      <ReactGrid
        rowData={rowData}
        ref={gridRef}
        gridOptions={agGridOptions}
        onGridReady={onGridReady}
        onRowClicked={onRowClicked}
        onRowSelected={onRowSelected}
        onCellClicked={onCellClicked}
        onSelectionChanged={onSelectionChanged}
        loadingCellRenderer={loadingCellRenderer}
        pagination={gridOptions?.pagination ?? true}
        paginationPageSize={
          gridOptions?.paginationPageSize || paginationPageSize
        }
      />
    </Box>
  );
};

const MemoizedDataGrid = React.memo(DataGrid);
export { MemoizedDataGrid as DataGrid };
